import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Svg, TempurLogoTextBlack, TempurLogotypeWhite } from "../svg";

import styles from "./JdPower2022Blocks.module.scss";

/**
 * Native ports of the four ``hc-block--jdpower-2022--block-*`` partials.
 *
 * No longer in ``HARDCODED_CHOICES`` but their partials still existed, so any
 * persisted page that references them keeps rendering. Registered by their full
 * ``hc-block--...`` version string.
 *
 * Legacy ``static_image`` renders EAGER under puppeteer; image ``src`` values
 * are built from the reactivated ``STATIC_URL`` context (``useStaticURL``) --
 * SSR-safe and byte-identical to the legacy ``{% static %}`` output, unlike a
 * Vite asset import. ``width``/``height`` preserved verbatim. The
 * block svg includes pass only ``src`` + ``class`` (no description / title /
 * ariaHidden), so the icon renders with just its class -- no screenreader span,
 * no ``<title>``.
 *
 * NOTE: block-dark's legacy ``{% if footer is None %}`` guard is ALWAYS TRUE --
 * ``footer`` is never in the render context, and an undefined variable resolves
 * to ``None`` in a Django ``{% if %}`` -- so block-dark DOES render its footer
 * (like block-light). Only the two in-store variants omit the footer.
 */

const Jdpower2022Footer = (): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <footer>
            <img
                src={`${staticURL}img/hardcoded/jdpower-2022--double.png`}
                alt="2022 JD Power Award Trophy"
                width="168px"
                loading="lazy"
                decoding="async"
            />
            <p>
                Plus, #1 in Customer Satisfaction with Mattresses Purchased
                Online in 2022 & 2021<sup>+</sup>
            </p>
        </footer>
    );
};

export const JdPower2022BlockDark = (): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <div className={concatClassNames([styles.block, styles.blockDark])}>
            <div>
                <Svg
                    Icon={TempurLogotypeWhite}
                    src="tempur-logotype-white"
                    className={styles.logo}
                />
                <h2>#1 in Customer Satisfaction</h2>
                <h3>with Mattresses Purchased In-Store</h3>
                <p>
                    — Awarded by J.D. Power in 2022-2019 & 2017<sup>+</sup>
                </p>
                <img
                    src={`${staticURL}img/hardcoded/jdpower-2022--row-of-five.png`}
                    width="630px"
                    height="210px"
                    alt="2022, 2021, 2020, 2019 & 2017 JD Power Award Trophies"
                    className={concatClassNames([
                        styles.heroImage,
                        "responsive-img",
                    ])}
                    loading="lazy"
                    decoding="async"
                />
            </div>
            <Jdpower2022Footer />
        </div>
    );
};

export const JdPower2022BlockInStoreDark = (): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <div
            className={concatClassNames([
                styles.block,
                styles.inStoreBlockDark,
            ])}
        >
            <div>
                <Svg
                    Icon={TempurLogotypeWhite}
                    src="tempur-logotype-white"
                    className={styles.logo}
                />
                <h2>#1 in Customer Satisfaction</h2>
                <h3>with Mattresses Purchased In-Store</h3>
                <p>
                    — Awarded by J.D. Power in 2022-2019 & 2017<sup>+</sup>
                </p>
                <img
                    src={`${staticURL}img/hardcoded/jdpower-2022--row-of-five.png`}
                    width="630px"
                    height="210px"
                    alt="2022, 2021, 2020, 2019 & 2017 JD Power Award Trophies"
                    className={concatClassNames([
                        styles.heroImage,
                        "responsive-img",
                    ])}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </div>
    );
};

export const JdPower2022BlockInStoreLight = (): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <div
            className={concatClassNames([
                styles.block,
                styles.inStoreBlockLight,
            ])}
        >
            <div>
                <Svg
                    Icon={TempurLogoTextBlack}
                    src="tempur-logo-text-black"
                    className={styles.logo}
                />
                <h2>#1 in Customer Satisfaction</h2>
                <h3>with Mattresses Purchased In-Store</h3>
                <p>
                    — Awarded by J.D. Power in 2022-2019 & 2017<sup>+</sup>
                </p>
                <img
                    src={`${staticURL}img/hardcoded/jdpower-2022--row-of-five.png`}
                    width="630px"
                    height="210px"
                    alt="2022, 2021, 2020, 2019 & 2017 JD Power Award Trophies"
                    className={concatClassNames([
                        styles.heroImage,
                        "responsive-img",
                    ])}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </div>
    );
};

export const JdPower2022BlockLight = (): React.JSX.Element => {
    const staticURL = useStaticURL();
    return (
        <div className={concatClassNames([styles.block, styles.blockLight])}>
            <div>
                <Svg
                    Icon={TempurLogoTextBlack}
                    src="tempur-logo-text-black"
                    className={styles.logo}
                />
                <h2>#1 in Customer Satisfaction</h2>
                <h3>with Mattresses Purchased In-Store</h3>
                <p>
                    — Awarded by J.D. Power in 2022-2019 & 2017<sup>+</sup>
                </p>
                <img
                    src={`${staticURL}img/hardcoded/jdpower-2022--row-of-five.png`}
                    width="630px"
                    height="210px"
                    alt="2022, 2021, 2020, 2019 & 2017 JD Power Award Trophies"
                    className={concatClassNames([
                        styles.heroImage,
                        "responsive-img",
                    ])}
                    loading="lazy"
                    decoding="async"
                />
            </div>
            <Jdpower2022Footer />
        </div>
    );
};
